import React, { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { cn } from "../../lib/utils";

// Add CSS variables to :root in index.css
const styleVariables = `
  :root {
    /* 1. Navbar background blur and colors */
    --navbar-bg-opacity: 0.4;
    --navbar-blur: 5px;
    --navbar-bg: rgba(0, 0, 0, var(--navbar-bg-opacity));
    --navbar-border-color: rgba(222, 222, 222, 0.45);
    
    /* 2. Active section highlight */
    --active-bg-opacity: 0.7;
    --active-bg: rgba(0, 0, 0, var(--active-bg-opacity));
    
    /* 3. Text colors */
    --nav-text: rgba(255, 255, 255, 0.84);
    --nav-text-hover: white;
    --nav-text-active: white;
    
    /* 4. Light bar effects */
    --light-bar-color: darkblue;
    --light-bar-glow-opacity: 0.15;
    --light-bar-blur: 10px;

    /* 5. Shadow controls */
    --navbar-shadow-x: 0px;
    --navbar-shadow-y: 2px;
    --navbar-shadow-blur: 4px;
    --navbar-shadow-spread: 0px;
    --navbar-shadow-color: rgba(0, 0, 0, 0.5);

    /* 6. Desktop center shift (positive moves navbar to the right) */
    --navbar-center-shift: 48px;
  }
`;

// Insert styles into document
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = styleVariables;
  document.head.appendChild(style);
}

const NavBar = ({ items, className, enableScrollBehavior = false }) => {
  const [isMobile, setIsMobile] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const navRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!isMobile) return;

    const handleScroll = () => {
      const scrollY = window.scrollY;
      const viewportHeight = window.innerHeight;
      const stickyPoint = viewportHeight * 0.15; // Stick when 20% from top of viewport

      if (scrollY > viewportHeight - stickyPoint) {
        setIsSticky(true);
      } else {
        setIsSticky(false);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMobile]);

  const navStyle = isMobile ? {
    position: isSticky ? 'fixed' : 'absolute',
    left: '50%',
    transform: 'translateX(-50%)',
    ...(isSticky ? {
      top: '24px',
      bottom: 'auto'
    } : {
      top: 'auto',
      bottom: '24px'
    }),
    transition: 'all 0.3s ease'
  } : {
    position: 'fixed',
    top: 0,
    left: 'calc(50% + var(--navbar-center-shift))',
    transform: 'translateX(-50%)'
  };

  return (
    <div
      ref={navRef}
      className={cn(
        "z-50 pointer-events-none",
        !isMobile && "sm:pt-6",
        className
      )}
      style={navStyle}
    >
      <div 
        className="flex items-center gap-3 py-1 pl-3 pr-1 rounded-full pointer-events-auto"
        style={{
          background: `var(--navbar-bg)`,
          backdropFilter: `blur(var(--navbar-blur))`,
          border: `1px solid var(--navbar-border-color)`,
          boxShadow: `var(--navbar-shadow-x) var(--navbar-shadow-y) var(--navbar-shadow-blur) var(--navbar-shadow-spread) var(--navbar-shadow-color)`
        }}
      >
        {items.map((item) => {
          const Icon = item.icon;
          const isActive = item.isActive;

          return (
            <a
              key={item.name}
              href={item.url}
              data-section={item.section}
              onClick={(e) => {
                e.preventDefault();
                item.onClick?.();
              }}
              style={{
                color: `var(--nav-text)`,
                '--active-bg-color': isActive ? `var(--active-bg)` : 'transparent'
              }}
              className={cn(
                "relative cursor-pointer text-sm font-semibold px-4 py-2 rounded-full transition-colors whitespace-nowrap flex items-center",
                "hover:text-[var(--nav-text-hover)]",
                isActive && "text-[var(--nav-text-active)]"
              )}
            >
              <span className="hidden menu:inline">{item.name}</span>
              <span className="menu:hidden">
                <Icon size={18} strokeWidth={2.5} />
              </span>
              {isActive && (
                <motion.div
                  layoutId="lamp"
                  className="absolute inset-0 w-full rounded-full -z-10"
                  initial={false}
                  transition={{
                    type: "spring",
                    stiffness: 300,
                    damping: 30,
                  }}
                  style={{
                    background: `var(--active-bg)`
                  }}
                >
                  <div 
                    className="absolute -top-2 left-1/2 -translate-x-1/2 w-8 h-1 rounded-t-full"
                    style={{
                      background: `var(--light-bar-color)`
                    }}
                  >
                    <div 
                      className="absolute w-12 h-6 rounded-full -top-2 -left-2"
                      style={{
                        background: `var(--light-bar-color)`,
                        opacity: `var(--light-bar-glow-opacity)`,
                        filter: `blur(var(--light-bar-blur))`
                      }}
                    />
                  </div>
                </motion.div>
              )}
            </a>
          );
        })}
      </div>
    </div>
  );
};

export default NavBar; 